import { useCallback, useContext, useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { Grid } from '@mui/material';
import { useAPI } from 'src/services/api';
import AuthContext from 'src/store/auth-context';
import ToastContext from 'src/store/toast-context';
import DeadlineInfo from 'src/components/DeadlineInfo';
import LockedMessage from 'src/components/LockedMessage';
import Logo from 'src/components/Logo';
import LoginFormCard from 'src/components/LoginFormCard';
import LoginInfoCard from 'src/components/LoginInfoCard';
import Footer from 'src/components/layout/Footer';
import LoginModal from 'src/components/modal/LoginModal';
import 'src/styles/Login.scss';
import { useLoaded } from 'src/store/load-context';
import LoginForm from 'src/components/LoginForm';
import { stringValidation } from 'src/services/validation';

import MuiValidTextField from 'src/components/input/MuiValidTextField';
import * as Yup from 'yup';
const loginSchema = Yup.object().shape({
  accountNo: stringValidation(),
  pin: stringValidation(),
});

const initialValues = {
  accountNo: '',
  pin: '',
};
const LoginPage = () => {
  const api = useAPI();
  const auth = useContext(AuthContext);
  const history = useHistory();
  const [locked, setLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [estimated, setEstimated] = useState(false);
  const [affidavit, setAffidavit] = useState(false);
  const { setLoaded } = useLoaded();
  const { handleError, error } = useContext(ToastContext);
  const redirectPath = new URLSearchParams(history.location.search).get('redirect') ?? '/';

  const closeModal = () => {
    setModalOpen(false);
  };

  const getLockStatus = useCallback(async () => {
    try {
      const response = await api.get('/application/lockStatus');
      return response.data;
    } catch (err) {
      handleError(err);
    }
  }, [api, handleError]);

  useEffect(() => {
    const loadLockStatus = async () => {
      try {
        const { status } = await getLockStatus();
        setLocked(status);
      } catch (error) {
        handleError(error);
      } finally {
        setLoaded(true);
      }
    };
    loadLockStatus();
  }, [getLockStatus, handleError, setLoaded]);

  const accountInGoodStanding = async ({ accountNo, pin }) => {
    const { data } = await api.post('/account/status', { accountNo, pin });

    if (data.length === 0) throw Error('Invalid account number or pin');

    const [status] = data?.status;
    const [filing] = data?.filing;

    if (status?.accountStatus === 'I') {
      setModalTitle('Your account is INACTIVE for the current tax year');
      setModalOpen(true);
      return false;
    }

    if (status?.recdate !== '01/01/2000 00:00:00' && status?.webdate === '01/01/2000 00:00:00') {
      setModalTitle('We have processed an affidavit on this account that was not from the web');
      setAffidavit(true);
      setModalOpen(true);
      return false;
    }

    if (status?.pastdue > 0 && filing?.signed !== 'Y') {
      setModalTitle(
        'The filing deadline has passed, the account will be estimated in accordance with the state statute for the current tax year.'
      );
      setModalOpen(true);
      return false;
    }

    if (Number(status?.estimated) === 1) {
      setModalTitle('Your account has been estimated for the current tax year');
      setEstimated(true);
      setModalOpen(true);
      return false;
    }
    return true;
  };

  const login = async ({ accountNo, pin }) => {
    accountNo = accountNo.trim();
    pin = pin.trim();

    try {
      setSubmitting(true);
      if (!(await accountInGoodStanding({ accountNo, pin }))) {
        setSubmitting(false);
        return;
      }
      await api.post('/account/login', { accountNo, pin });
      const response = await api.post('/account/accessToken', { accountNo, pin });
      auth.login(response.headers['x-access-token'], response.data.exp);
      history.replace(redirectPath);
    } catch (err) {
      if (err.response?.status === 400) error('Invalid account number or pin');
      else {
        handleError(err);
      }
    }
    setSubmitting(false);
  };

  if (auth.isLoggedIn) {
    return <Redirect to="/home" />;
  }

  return (
    <>
      {/* center everything */}
      <LoginModal
        open={modalOpen}
        onClose={closeModal}
        title={modalTitle}
        estimated={estimated}
        affidavit={affidavit}
      />
      <Grid
        className="main-container"
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '100vh' }}
      >
        <header style={{ marginTop: '2rem' }}>
          <Logo />
        </header>
        <Grid
          sx={{
            spacing: '0.25rem',
            my: '0.125rem',
            flexDirection: 'column',
            justify: 'center',
            width: '21rem',
          }}
        >
          <DeadlineInfo />
          {locked ? (
            <LockedMessage />
          ) : (
            <>
              <LoginFormCard>
                <LoginForm
                  onSubmit={login}
                  loaded={!submitting}
                  initialValues={initialValues}
                  schema={loginSchema}
                  fields={[
                    {
                      name: 'accountNo',
                      component: MuiValidTextField,
                      label: 'Account Number',
                      variant: 'standard',
                      'data-cy': 'accountNumber',
                    },
                    {
                      name: 'pin',
                      component: MuiValidTextField,
                      label: 'PIN',
                      variant: 'standard',
                      'data-cy': 'pin',
                    },
                  ]}
                />
              </LoginFormCard>
              <LoginInfoCard />
            </>
          )}
        </Grid>
        <Footer />
      </Grid>
    </>
  );
};

export default LoginPage;
